/**
 * 後端錯誤碼 → 給操作者看的中文（批155 起的共用對照）。
 *
 * 各頁原本各有一份 `errText`（`RequestsView` 的 `ERRORS`、`ReissueModal` 的分支…），那些是
 * 「只在那一頁出現」的碼。這裡放的是**會在好幾個畫面出現**的碼——同一個碼在套裝編輯、換證、
 * 審核、加購四個地方各寫一次中文，很快就會說成四種話。
 *
 * 用法：`knownErrorText(detail)` 認得就回中文，不認得回 `null`，呼叫端照舊退回自己的處理
 * （多半是印後端的 `message`）。★ 這裡只做**文案**，不是權限判斷。
 */
import { ApiError } from '@/api/client'

export interface ErrorDetail {
  code?: string
  message?: string
  key?: string
  label?: string
  module?: string
  plan?: string
  limit?: number | null
  value?: number | null
  field?: string
  /** `addon_quota_step`：哪個配額鍵、級距多少（`value` 是收到的值）。 */
  quota?: string
  step?: number
}

/** 錯誤物件 → `detail`（字串碼也包成 `{code}`）；沒有 detail 回 null。 */
export function errorDetailOf(e: unknown): ErrorDetail | null {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return null
  if (typeof d === 'string') return { code: d }
  if (typeof d === 'object') return d as ErrorDetail
  return null
}

function named(d: ErrorDetail, fallback = '其中一項'): string {
  return d.label || d.key || fallback
}

/**
 * 碼 → 中文。值可以是字串或「吃 detail 的函式」（訊息要帶出是哪一個鍵／模組時）。
 *
 * 批155 契約（`temp/batch155/CONTRACT.md`）會在後台看到的碼都在這裡；平台 realm 專屬的碼
 * （`quota_not_fillable`／`expires_at_not_allowed`／`addon_requires_review`…）也列著——
 * 後台加購走同一套驗證，告警與稽核的詳情也會帶到它們。
 */
export const ERROR_CODE_TEXT: Record<string, string | ((d: ErrorDetail) => string)> = {
  // --- 自動核准與內部套裝／模組 ---
  auto_approve_not_applicable: '「自動核准」只適用於種類是「套裝」的列；試用與加購模板不能打開。',
  internal_plan_requires_manager:
    '內部套裝與內部模組只有「營運主管」以上能用來核發、換證或在審核時修改內容。請改選一般套裝與模組，或請營運主管處理。',
  unknown_plan: '選的套裝不屬於這個產品（或不開放在這裡使用）。',
  unknown_module: (d) =>
    d.module ? `模組「${d.module}」不屬於這個產品（或不開放在這裡使用）。` : '有模組不屬於這個產品（或不開放在這裡使用）。',
  // --- 子證模板與上限 ---
  unknown_addon_plan: (d) =>
    d.plan ? `找不到子證模板「${d.plan}」（可能已停用，或是內部模板）。` : '找不到這個子證模板（可能已停用，或是內部模板）。',
  // 後端帶 `reason`（unknown_key／bad_value／empty／not_addon…）與中文 `message`——訊息已指名是哪一項。
  addon_limits_invalid: (d) =>
    d.message ?? `子證上限的設定不正確（${named(d)}）：每一項至少填一個上限，而且必須是 0 以上的整數。`,
  follow_main_expiry_not_applicable: '「跟隨主證到期」只適用於種類是「加購」的模板。',
  module_spec_invalid: (d) => d.message ?? `模組「${named(d)}」的設定不正確。`,
  supersedes_invalid:
    '要取代的那張子證不符合條件：必須是同一台主機、同一個子證模板，而且仍然有效。請重新整理後再選一次。',
  quota_not_fillable: (d) => `「${named(d)}」不在這個子證模板開放填寫的項目裡。`,
  expires_at_not_allowed: '這個子證模板的期限不是「依送件指定」，不能另外指定到期日。',
  addon_requires_review: (d) =>
    `「${named(d)}」的數值${d.value != null ? ` ${d.value}` : ''}超過模板上限${d.limit != null ? ` ${d.limit}` : ''}，需要人工處理。`,
  // 加購數量級距（11-01 裁示）：後台加購、代理商／審核核發、兌換活動、加購模板與憑證設計共用這個碼。
  // 後端的 `message` 已經用中文名稱指名是哪一項；模板相關的錯誤另帶 `plan`。
  addon_quota_step: (d) => {
    const base =
      d.message ??
      (d.step != null
        ? `「${d.quota ?? '其中一項'}」的加購數量必須是 ${d.step} 的倍數（${d.step}、${d.step * 2}、${d.step * 3}…）。`
        : '加購數量不是級距的倍數。')
    return d.plan ? `加購模板「${d.plan}」：${base}` : base
  },
  // --- 平台 realm 的狀態類 ---
  no_active_license: '這個組織目前沒有有效的主證。',
  addon_not_active: '這張子證已經不是有效狀態（可能已被取代或撤銷）。',
  addon_not_found: '找不到這張子證。',
}

/** 認得的碼回中文，不認得回 `null`（呼叫端退回自己的處理）。 */
export function knownErrorText(detail: unknown): string | null {
  if (detail == null) return null
  const d: ErrorDetail = typeof detail === 'string' ? { code: detail } : (detail as ErrorDetail)
  const entry = d.code ? ERROR_CODE_TEXT[d.code] : undefined
  if (entry == null) return null
  return typeof entry === 'function' ? entry(d) : entry
}

/** `knownErrorText` 的錯誤物件版本。 */
export function knownErrorTextOf(e: unknown): string | null {
  return knownErrorText(errorDetailOf(e))
}
